<!--
  The expected-letter sequence as an ordered chip list, with a cursor on the current letter.

  This is the guided palette: the annotator draws a box and the chip under the cursor is
  assigned automatically. There is no typing and no menu anywhere in the flow.

  Clicking a chip **only moves the cursor.** It used to relabel the selected box whenever one
  was selected — and because `addBox` selects the box it just drew, one always was. So the most
  ordinary gesture in the tool (draw a box, click a chip further along to jump ahead) silently
  changed the letter identity of the box just drawn, and said nothing when the target chip
  happened to be free. Relabelling is now an explicit act: `L`, Shift+click, or right-click.
  See `session.relabelSelected`.

  `{}` chips are pre-flagged uncertain and `[]` chips restored — the class is unchanged. `{ṣ}`
  is the class `ṣ` with a flag, never a separate class.

  Colour is not the only signal: state is carried by the border style and by a text badge.
-->
<script lang="ts">
  import type { TokenOut } from '../lib/api/types';
  import {
    chipAction,
    isLacunaToken,
    readingFlow,
    tokenLabel,
    unknownTokenHint,
  } from '../lib/draft';
  import { glyphOrientation, orientationNote } from '../lib/glyphs';
  import { blurOnPointerActivation } from '../lib/keymap';
  import { href } from '../lib/router.svelte';
  import { glyphPref } from '../lib/stores/glyphPref.svelte';
  import { session } from '../lib/stores/session.svelte';
  import GlyphImage from './GlyphImage.svelte';

  /**
   * Whether the key is open, remembered across images and sessions.
   *
   * Per browser rather than per account: it tracks how familiar this person is with the tool,
   * which is a property of the person, and they are the only one using this browser profile.
   * Reading it lazily and guarding `localStorage` keeps the component mountable in jsdom,
   * where the dom tests run it.
   */
  const KEY_OPEN_STORAGE = 'hismaic.palette.keyOpen';

  function readKeyOpen(): boolean {
    try {
      return localStorage.getItem(KEY_OPEN_STORAGE) === 'true';
    } catch {
      return false;
    }
  }

  let keyOpen = $state(readKeyOpen());

  $effect(() => {
    const value = keyOpen;
    try {
      localStorage.setItem(KEY_OPEN_STORAGE, String(value));
    } catch {
      // A browser with storage disabled still gets a working disclosure, just a forgetful one.
    }
  });

  const tokens = $derived(session.tokens);

  /**
   * Which way the reference forms face.
   *
   * Right-to-left is the reference orientation and the caption says so, because that is a
   * convention this project adopted rather than a fact about Hismaic. Writing that runs against
   * it is mirrored; writing that runs vertically is a quarter turn off it.
   *
   * Display only. What a **box** records is its `chirality`, judged per box against the stored
   * reference form and derived from nothing — least of all from this. See the note on
   * `glyphOrientation`.
   */
  const orientation = $derived(glyphOrientation(session.readingDirection));

  /**
   * Which way the strip itself runs.
   *
   * Not the same question as which way the forms face, and not the same answer: the stored form's
   * reference orientation is `rtl` while the document's is left-to-right, so `rtl` reverses this
   * strip and leaves the glyphs alone. See `readingFlow`.
   *
   * The reversal is CSS, never a reversed array. The chips stay in reading order in the DOM, so
   * the numbering, the listbox semantics and the order a screen reader announces are all
   * untouched — only the layout moves. It also puts the word gaps in the right places for free:
   * a separator between two items stays between them however the line is laid out.
   */
  const flow = $derived(readingFlow(session.readingDirection));
  const boxedByToken = $derived.by(() => {
    const map = new Map<number, number>();
    for (const box of session.boxes) {
      if (box.alignment === 'expected' && box.expectedTokenIndex !== null) {
        map.set(box.expectedTokenIndex, (map.get(box.expectedTokenIndex) ?? 0) + 1);
      }
    }
    return map;
  });

  function tokenState(token: TokenOut): 'boxed' | 'absent' | 'open' {
    if (session.absentSet.has(token.index)) return 'absent';
    if (boxedByToken.has(token.index)) return 'boxed';
    return 'open';
  }

  function describe(token: TokenOut): string {
    const parts = [`letter ${token.index + 1} of ${tokens.length}`, `class ${token.display}`];
    if (token.uncertain) parts.push('marked uncertain by the editor');
    if (token.restored) parts.push('editorially restored');
    if (token.letter === 'illegible') parts.push('illegible stretch of unknown length');
    const unknownHint = unknownTokenHint(token);
    if (unknownHint) parts.push(unknownHint);
    parts.push(
      tokenState(token) === 'boxed' ? 'boxed' : tokenState(token) === 'absent' ? 'recorded as not present' : 'not yet resolved',
    );
    parts.push(
      session.selected
        ? 'click to move the cursor here; shift-click or right-click to relabel the selected box onto it'
        : 'click to move the cursor here',
    );
    return parts.join(', ');
  }

  /**
   * A plain click moves the cursor. Full stop — a class change is never a side effect of
   * navigating. Shift+click (or right-click) is the deliberate relabel; `L` is the same action
   * from the keyboard, on the chip under the cursor. The policy itself is `chipAction`, in
   * `lib/draft.ts`, so it is pinned by a test rather than by this line of markup.
   */
  function onChipClick(token: TokenOut, event: MouseEvent): void {
    const action = chipAction({ kind: 'click', shiftKey: event.shiftKey }, Boolean(session.selected));
    if (action === 'relabel-selected') session.relabelSelected(token.index);
    else session.setCursor(token.index);
    blurOnPointerActivation(event);
  }

  function onChipContextMenu(token: TokenOut, event: MouseEvent): void {
    const action = chipAction({ kind: 'contextmenu' }, Boolean(session.selected));
    if (action !== 'relabel-selected') return;
    event.preventDefault();
    session.relabelSelected(token.index);
  }

  /** Word boundaries are a gap between chips, not a chip: a space emits no token. */
  function startsNewWord(token: TokenOut, index: number): boolean {
    const previous = tokens[index - 1];
    return previous !== undefined && previous.word_index !== token.word_index;
  }
</script>

<div class="strip-wrap">
  <div class="row wrap head">
    <h3>Expected letters</h3>
    <span class="muted small">
      from OCIANA's transliteration — a weak label, not ground truth
    </span>
    <span class="spacer"></span>
    <span class="small mono">
      {session.progress.resolved}/{session.progress.expected_letters} resolved
    </span>
  </div>

  {#if tokens.length > 0}
    <div class="row wrap forms-row">
      <label class="row small forms-toggle">
        <input
          type="checkbox"
          data-testid="show-glyph-forms"
          checked={glyphPref.show}
          onchange={(event) => glyphPref.set(event.currentTarget.checked)}
        />
        Hismaic forms
      </label>
      {#if glyphPref.show}
        <span class="muted small">{orientationNote(session.readingDirection)}</span>
      {/if}
    </div>
  {/if}

  {#if tokens.length === 0}
    <p class="muted small">
      This record has no transliteration, so there is no expected sequence. Every box drawn here
      is recorded as an unexpected letter and its class is set in the inspector.
    </p>
  {:else}
    <div
      class="chips"
      class:vertical={flow.vertical}
      class:reversed={flow.reversed}
      role="listbox"
      aria-label="Expected letter sequence"
    >
      {#each tokens as token, index (token.index)}
        {#if startsNewWord(token, index)}
          <span class="word-gap" aria-hidden="true"></span>
        {/if}
        <button
          type="button"
          role="option"
          aria-selected={session.cursor === token.index}
          class="chip {tokenState(token)}"
          class:cursor={session.cursor === token.index}
          class:uncertain={token.uncertain}
          class:restored={token.restored}
          class:lacuna={isLacunaToken(token)}
          title={describe(token)}
          aria-label={describe(token)}
          onclick={(event) => onChipClick(token, event)}
          oncontextmenu={(event) => onChipContextMenu(token, event)}
        >
          <span class="index small mono">{token.index + 1}</span>
          <span class="glyph letter">{tokenLabel(token)}</span>
          {#if glyphPref.show}
            <GlyphImage
              letterClass={token.letter_class}
              mirrored={orientation.mirrored}
              turned={orientation.turned}
              height={28}
              label={token.display}
            />
          {/if}
          <span class="marks small">
            {#if token.uncertain}<span title="uncertain reading">?</span>{/if}
            {#if token.restored}<span title="editorially restored">[ ]</span>{/if}
            {#if tokenState(token) === 'boxed'}<span title="boxed">✓</span>{/if}
            {#if tokenState(token) === 'absent'}<span title="recorded as not present">∅</span>{/if}
            {#if (boxedByToken.get(token.index) ?? 0) > 1}<span>!</span>{/if}
          </span>
        </button>
      {/each}
    </div>

    <!--
      The key and the relabel policy, on request.

      Both are reference: true, worth saying, and worth reading roughly twice. Permanently
      open they were seven lines of the rail wrapped around five chips, and permanent text is
      what people learn to stop reading — which is the failure mode that matters here, because
      the relabel policy is the one thing on this panel somebody can get wrong silently.

      It is still reachable in one click, its state is remembered, and every chip carries the
      same information in its `title` and its `aria-label` regardless. The conditional banners
      below are a different thing and stay unconditional: they fire on the specific chip that
      is about to be got wrong, which is teaching at the moment of need rather than reference.
    -->
    <details class="keyhelp" bind:open={keyOpen}>
      <summary class="small muted">Key and relabelling</summary>
      <div class="legend small muted">
        <span><span class="swatch cursor-swatch"></span> cursor</span>
        <span><span class="swatch boxed-swatch"></span> ✓ boxed</span>
        <span><span class="swatch absent-swatch"></span> ∅ not present</span>
        <span>? uncertain <span class="mono">{'{ }'}</span></span>
        <span>[ ] restored</span>
      </div>

      <p class="small muted relabel-note">
        Clicking a chip <strong>only moves the cursor</strong>. To change the class of the
        selected box, press <kbd>L</kbd> (relabels onto the chip under the cursor), or
        shift-click / right-click the chip you want. A letter identity never changes as a side
        effect of navigating.
      </p>
      <p class="small muted relabel-note">
        The form under each letter is OCIANA's font rendering — a reference for reading the
        transliteration, <strong>not a picture of this stone</strong>. It is stored in the
        right-to-left reference orientation: it is flipped here when the record reads left to
        right, and turned a quarter when the record reads vertically. The strip runs the record's
        way too — right to left, or stacked — while the numbers stay the reading order.
        <em>Illegible</em> and <em>unknown</em> have no form and show an empty tile: there
        is no letter to draw. <a href={href({ name: 'home', tab: 'alphabet' })}>The full
          alphabet</a>, with a drill.
      </p>
      <p class="small muted relabel-note">
        <a href={href({ name: 'home', tab: 'conventions' })}>The three conventions people get
          wrong</a>, with worked examples · <a
          href={href({ name: 'guidelines', at: '6-reading-the-chip-strip-what-ocianas-marks-mean' })}
        >
          what these marks mean
        </a>
      </p>
    </details>

    <!--
      An `unknown` chip is not a lacuna, though `isLacunaToken` groups the two for cursor
      stickiness. It stands for one mark whose letter identity the project could not determine —
      and the two kinds of `unknown` in this corpus want opposite things from the annotator, so
      the note says which one this is. See `unknownTokenHint`.
    -->
    {#if session.currentToken && unknownTokenHint(session.currentToken)}
      <p class="banner info small lacuna-note">
        <strong>Unidentified mark.</strong>
        {unknownTokenHint(session.currentToken)}. It is deliberately not read as a letter: OCIANA
        did not name it, so the tool does not either. Mark it not present (<kbd>N</kbd>) if there
        is no glyph.
      </p>
    {:else if session.currentToken && isLacunaToken(session.currentToken)}
      <p class="banner info small lacuna-note">
        <strong>Illegible stretch.</strong> This chip stands for an illegible run of
        <em>unknown</em> length — not one letter. Draw as many boxes as there are glyphs: the
        first takes the chip, the rest are recorded as unexpected letters. Draw none and mark it
        not present (<kbd>N</kbd>) if nothing is boxable. The cursor stays here until you move it.
      </p>
    {/if}
  {/if}
</div>

<style>
  .strip-wrap {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .head {
    align-items: baseline;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-height: 40vh;
    overflow-y: auto;
    padding: 2px;
  }

  /*
   * The strip runs the way the record runs.
   *
   * Layout only — the chips are in reading order in the DOM in every mode, so the numbers on
   * them never move. `max-height` doubles as the break constraint a wrapping column needs, so a
   * long vertical record forms a second column to the right rather than one endless stack; once
   * the columns pass the rail, both axes scroll.
   */
  .chips.reversed {
    flex-direction: row-reverse;
  }

  .chips.vertical {
    flex-direction: column;
    overflow: auto;
    /* Columns are sized to what is in them and packed to the left. Without this a single
       column is a lone flex line, which stretches to the whole cross size — so a short record
       got chips the full width of the rail while a long one got tidy ones, from the same CSS. */
    align-content: flex-start;
  }

  .chips.vertical.reversed {
    flex-direction: column-reverse;
    /* No `justify-content`: main-start is the bottom here, which is where a bottom-to-top
       reading starts, so a part-filled second column fills from the bottom exactly as the
       first one did. */
  }

  .word-gap {
    width: 14px;
  }

  .chips.vertical .word-gap {
    width: auto;
    height: 14px;
  }

  .forms-row {
    align-items: center;
    gap: 10px;
  }

  .forms-toggle {
    gap: 5px;
    align-items: center;
    cursor: pointer;
  }

  .chip {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    min-width: 38px;
    padding: 2px 6px 3px;
    background: var(--bg-raised);
    border: 2px solid var(--line);
    border-radius: 6px;
    line-height: 1.15;
  }

  /*
   * Stacked chips lie down.
   *
   * A quarter-turned form is landscape — 28 wide by 21 tall where it was 21 by 28 — and a column
   * of portrait chips around landscape glyphs is mostly empty space. One row per letter: number,
   * letter, form, badges. Column items stretch to the widest chip on their own, so the column
   * squares up without a width being set anywhere.
   */
  .chips.vertical .chip {
    flex-direction: row;
    align-items: center;
    gap: 6px;
  }

  /* Right-aligned in a fixed gutter so 1 and 10 line up down the column. */
  .chips.vertical .index {
    min-width: 1.6em;
    text-align: right;
  }

  .chips.vertical .chip :global(.glyph-form) {
    margin-top: 0;
  }

  /*
   * The form sits at the right end of a landscape chip.
   *
   * `order: 1` puts it after the badges rather than before them, which is what makes the forms
   * line up down the column: a chip carrying `? ✓` would otherwise push its form further left
   * than its neighbour's. `margin-left: auto` takes the slack, so the right edge is the one
   * they align on. The empty tile goes with them — a lacuna holds the column's shape, which is
   * the whole reason it is drawn.
   */
  .chips.vertical .chip :global(.glyph-slot),
  .chips.vertical .chip :global(.no-form) {
    order: 1;
    margin-left: auto;
  }

  .chip .index {
    color: var(--text-dim);
    font-size: var(--text-xs);
  }

  /* Large enough that ʾ (U+02BE) and ʿ (U+02BF) are told apart at a glance: both are small
     raised marks, and they are separate vocabulary classes. */
  .chip .letter {
    font-size: 1.4rem;
  }

  .chip :global(.glyph-form) {
    margin-top: 2px;
  }

  /* The letter and its form are one statement, so a state that dims one dims both. */
  .chip.absent :global(.glyph-form) {
    opacity: 0.65;
  }

  .chip .marks {
    min-height: 1em;
    font-size: var(--text-xs);
    color: var(--text-dim);
    letter-spacing: 0.06em;
  }

  /* State is carried by border STYLE as well as colour. */
  .chip.uncertain {
    border-style: dashed;
    border-color: var(--uncertain);
  }

  .chip.restored {
    border-style: dotted;
    border-color: var(--restored);
  }

  .chip.lacuna .letter {
    color: var(--text-dim);
  }

  .chip.boxed {
    background: #1d2b22;
    border-color: var(--ok);
  }

  /*
   * Not-present: dotted border + the ∅ badge + a dimmed letter. The strike-through this used
   * to carry ran across the index, the letter and the badge alike, and a struck `ṭ` is very
   * nearly a `ṯ` — an unreadable palette is worse than a subtle one.
   */
  .chip.absent {
    background: #2a1f2b;
    border-color: var(--restored);
    border-style: dotted;
  }

  .chip.absent .letter {
    opacity: 0.65;
  }

  .chip.cursor {
    outline: 3px solid var(--accent);
    outline-offset: 1px;
  }

  .keyhelp > summary {
    cursor: pointer;
  }

  .keyhelp[open] > summary {
    margin-bottom: 8px;
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 4px;
    vertical-align: middle;
  }

  .cursor-swatch {
    border: 2px solid var(--accent);
  }
  .boxed-swatch {
    border: 2px solid var(--ok);
  }
  .absent-swatch {
    border: 2px dotted var(--restored);
  }

  .lacuna-note {
    margin: 0;
  }

  .relabel-note {
    margin: 0;
  }
</style>
